import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

/**
 * Product collage — served from `public/img/nrs/` (source: repo `assets/neurorobotics_studio.jpg`).
 * Used on /nrs hub.
 */
export const NRS_HERO_COLLAGE_BG = "/img/nrs/neurorobotics_studio.jpg";

/**
 * Download page hero — served from `public/img/nrs/` (source: repo `assets/Cosmic waves and distant galaxies.jpg`).
 * Used on `/nrs/download` only.
 */
export const NRS_DOWNLOAD_HERO_BG = "/img/nrs/cosmic-waves-distant-galaxies.jpg";

/** Full-bleed shell: collage + scrim + subtle primary radial (::after). */
export const nrsHubHeroShellSx: SxProps<Theme> = {
  position: "relative",
  overflow: "hidden",
  backgroundColor: "background.default",
  backgroundImage: (theme) =>
    `linear-gradient(180deg, ${alpha(theme.palette.common.black, 0.42)} 0%, ${alpha(theme.palette.background.default, 0.58)} 38%, ${alpha(theme.palette.background.default, 0.9)} 100%), url(${NRS_HERO_COLLAGE_BG})`,
  backgroundSize: "cover",
  backgroundPosition: "center center",
  backgroundRepeat: "no-repeat",
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    background: (theme) =>
      `radial-gradient(ellipse 85% 55% at 50% -15%, ${alpha(theme.palette.primary.main, 0.18)} 0%, transparent 50%)`,
    pointerEvents: "none",
  },
};

/** Same treatment as `nrsHubHeroShellSx` but cosmic artwork for the download page. */
export const nrsDownloadHeroShellSx: SxProps<Theme> = {
  ...nrsHubHeroShellSx,
  backgroundImage: (theme) =>
    `linear-gradient(168deg, ${alpha(theme.palette.common.black, 0.48)} 0%, ${alpha(theme.palette.background.default, 0.62)} 42%, ${alpha(theme.palette.background.default, 0.88)} 100%), url(${NRS_DOWNLOAD_HERO_BG})`,
};

/** Canonical product name for desktop installers linked from /nrs (keep in sync with marketing). */
export const NRS_DESKTOP_EDITION_PRODUCT_NAME =
  "Neurorobotics Studio Desktop Edition";

/** Short product name (no “Desktop Edition”) — downloads, compact UI. */
export const NRS_STUDIO_PRODUCT_NAME_SHORT = "Neurorobotics Studio";

/**
 * Same product name for hero display: first line "Neurorobotics Studio", second "Desktop Edition".
 * Use with `white-space: pre-line` on the heading (see NrsPageHeader `titleSxHero`).
 */
export const NRS_DESKTOP_EDITION_PRODUCT_TITLE_DISPLAY =
  "Neurorobotics Studio\nDesktop Edition";

/** Public path — Neurorobotics Studio app icon (copied from feagi-desktop for /nrs marketing). */
export const NRS_HERO_LOGO_SRC = "/img/nrs-neurorobotics-studio-icon.png";
export const NRS_HERO_LOGO_ALT = "Neurorobotics Studio";

/**
 * Waitlist hero body (`/nrs/waitlist`): lead line + supporting paragraph.
 */
export const NRS_STUDIO_INTRO_SHORT = [
  'The shortest path from "What if?" to "It works."',
  "Don't let fragmented tooling stall your progress. Neurorobotics Studio gives you a powerful, local workspace to design AI models and test them instantly on simulated or physical robots. It's built to grow with you: intuitive enough for your first project, yet robust enough for your most complex builds.",
] as const;

/** Sharper text on retina / macOS; keeps Montserrat clean at marketing sizes. */
export const nrsCrispTextSx: SxProps<Theme> = {
  WebkitFontSmoothing: "antialiased",
  MozOsxFontSmoothing: "grayscale",
  textRendering: "geometricPrecision",
};

/** Primary marketing / explainer body — readable size, crisp rendering. */
export const nrsIntroBodySx: SxProps<Theme> = {
  ...nrsCrispTextSx,
  fontSize: { xs: "1.0625rem", sm: "1.125rem", md: "1.2rem" },
  lineHeight: { xs: 1.58, sm: 1.62 },
  letterSpacing: "-0.011em",
  fontWeight: 400,
  color: "text.secondary",
  overflowWrap: "anywhere",
  maxWidth: { xs: "min(38rem, 100%)", md: "min(58rem, 100%)" },
};

/** “How it works” and secondary bullets — slightly smaller than intro, still legible. */
export const nrsListBodySx: SxProps<Theme> = {
  ...nrsCrispTextSx,
  fontSize: { xs: "1rem", sm: "1.0625rem" },
  lineHeight: 1.68,
  letterSpacing: "-0.008em",
  color: "text.secondary",
  overflowWrap: "anywhere",
};

/** Panel: matches site dark surfaces; suppresses global Card hover weirdness */
export const nrsPanelSx: SxProps<Theme> = {
  width: "100%",
  minWidth: 0,
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: (theme) => alpha(theme.palette.common.white, 0.14),
  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.78),
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  boxShadow: "0 16px 42px rgba(0, 0, 0, 0.34)",
  backgroundImage: "none",
};

/** Page container: readable width, safe-area insets on notched phones, flex overflow safety */
export const nrsContainerSx: SxProps<Theme> = {
  maxWidth: "md",
  width: "100%",
  minWidth: 0,
  pl: {
    xs: "max(16px, env(safe-area-inset-left, 0px))",
    sm: 6,
  },
  pr: {
    xs: "max(16px, env(safe-area-inset-right, 0px))",
    sm: 6,
  },
  pb: { xs: "max(24px, env(safe-area-inset-bottom, 0px))", sm: 2 },
};

/** Clickable download tile — min 48px touch height on phones */
export const nrsPlatformTileSx: SxProps<Theme> = {
  ...nrsPanelSx,
  display: "block",
  textDecoration: "none",
  color: "text.primary",
  p: { xs: 2.5, sm: 3 },
  minHeight: { xs: 56, sm: "unset" },
  WebkitTapHighlightColor: "transparent",
  transition: "border-color 0.18s ease, box-shadow 0.18s ease, opacity 0.15s ease",
  "&:hover": {
    bgcolor: "background.paper",
    backgroundImage: "none",
    borderColor: "primary.main",
    boxShadow: (theme) =>
      `0 0 0 1px ${theme.palette.primary.main}40, 0 12px 40px rgba(0,0,0,0.35)`,
  },
  "&:active": {
    opacity: 0.92,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: "none",
  },
};
